// ─────────────────────────────────────────────────────────────────────────────
// DetectorProgress
//
// Shared UI for rendering a detector's "still collecting data" state across
// all four analytics tabs (Behavior, Risk, Context, Psychology). Replaces
// the old "Not enough data yet" stub with concrete progress and what's
// coming, so trial users see momentum instead of empty cards.
//
// Two pieces:
//   <ProgressBar />          — small animated progress bar
//   <InsufficientStatePanel /> — full panel rendered inside an insufficient
//                                detector card. Handles two cases:
//                                  a) requires_field exists → "Add X to your
//                                     trades to unlock" empty state
//                                  b) otherwise → "n of n_required trades —
//                                     keep logging"
//
// Plus one banner for the moderate-confidence case (early signal hedge):
//   <EarlySignalBanner />
// ─────────────────────────────────────────────────────────────────────────────

import { C } from "../lib/colors";

// Map a detector's `requires_field` value to a friendly explanation of what
// the user needs to do to unlock it. Falls back to a generic message if the
// field isn't in the map.
const REQUIRES_FIELD_GUIDE = {
  "trade.stop_loss": {
    label: "stop loss prices",
    where: "Trade Entry — Stop Loss field",
    why:   "Stop-related detectors compare your planned stops to your actual exits.",
  },
  "user_rules.max_daily_drawdown": {
    label: "a daily drawdown limit",
    where: "Profile → Manage Trading Rules",
    why:   "Sets the ceiling we compare your real drawdown against.",
  },
};

function describeRequiresField(field) {
  if (!field) return null;
  if (REQUIRES_FIELD_GUIDE[field]) return REQUIRES_FIELD_GUIDE[field];
  return {
    label: field.replace(/^trade\.|^user_rules\./, "").replace(/_/g, " "),
    where: null,
    why:   null,
  };
}

// ─── ProgressBar ─────────────────────────────────────────────────────────────
// Small horizontal bar. `value` and `max` are integers. Color shifts from
// muted → blue → green as the bar fills, giving low-progress cards a less
// urgent visual weight.

export function ProgressBar({ value, max, height = 6 }) {
  const safeMax = Math.max(1, max);
  const pct     = Math.max(0, Math.min(1, value / safeMax));
  const color   = pct >= 0.66 ? C.green : pct >= 0.33 ? C.blue : C.muted;

  return (
    <div
      style={{
        width: "100%",
        height,
        background: "#0b0d19",
        borderRadius: height / 2,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${pct * 100}%`,
          height: "100%",
          background: color,
          transition: "width 0.4s ease, background 0.3s ease",
        }}
      />
    </div>
  );
}

// ─── InsufficientStatePanel ──────────────────────────────────────────────────
// Renders inside a DetectorCard whose confidence is "insufficient". Replaces
// the otherwise-empty/stubbed expand body. Two layouts depending on whether
// the detector is gated on missing data (requires_field) or on sample size.

export function InsufficientStatePanel({ detector }) {
  const { n, n_required, requires_field, title } = detector;
  const guide = describeRequiresField(requires_field);

  // Case A: detector is waiting on a specific data field
  if (guide) {
    return (
      <div
        style={{
          padding: "14px 20px",
          background: `${C.muted}10`,
          borderTop: `1px solid ${C.border}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 18 }}>🔓</span>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Unlocks when you log {guide.label}
          </div>
        </div>
        {guide.where && (
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
            <strong style={{ color: C.text, fontWeight: 600 }}>Where:</strong> {guide.where}
          </div>
        )}
        {guide.why && (
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            <strong style={{ color: C.text, fontWeight: 600 }}>Why:</strong> {guide.why}
          </div>
        )}
      </div>
    );
  }

  // Case B: detector is collecting trades toward a sample-size threshold.
  // n_required can be 0 for the generic empty-detector fallback — in that
  // case we don't have a meaningful target so we keep it short.
  if (!n_required || n_required <= 0) {
    return (
      <div
        style={{
          padding: "14px 20px",
          background: `${C.muted}10`,
          borderTop: `1px solid ${C.border}`,
          fontSize: 12,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        Collecting data. {title} will appear as your trade history grows.
      </div>
    );
  }

  const remaining = Math.max(0, n_required - n);

  return (
    <div
      style={{
        padding: "14px 20px",
        background: `${C.muted}10`,
        borderTop: `1px solid ${C.border}`,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: 0.4 }}>
          Building your read on {title.toLowerCase()}
        </div>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: "monospace", whiteSpace: "nowrap" }}>
          {n} / {n_required}
        </div>
      </div>

      <ProgressBar value={n} max={n_required} />

      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
        {remaining === 0
          ? "Sample size reached — re-check after your next session."
          : `${remaining} more ${remaining === 1 ? "sample" : "samples"} needed for a confident read.`}
      </div>
    </div>
  );
}

// ─── EarlySignalBanner ───────────────────────────────────────────────────────
// Renders for "thin" and (optionally) "moderate" detectors above the existing
// summary. Tells the user "this is real but tentative — keep logging." Doesn't
// rewrite the detector's summary; sits above it as a hedge.

export function EarlySignalBanner({ detector }) {
  const { n, n_required } = detector;
  if (!n_required || n >= n_required) return null;

  const remaining = Math.max(0, n_required - n);
  if (remaining === 0) return null;

  return (
    <div
      style={{
        padding: "6px 10px",
        background: `${C.yellow}15`,
        border: `1px solid ${C.yellow}40`,
        borderRadius: 6,
        fontSize: 11,
        color: C.yellow,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        marginBottom: 8,
      }}
    >
      <span>⚡</span>
      <span>
        Early signal — {remaining} more {remaining === 1 ? "sample" : "samples"} for a confident read.
      </span>
    </div>
  );
}
